import * as ng from 'angular';
import * as _ from 'underscore';
import { StateService } from '@uirouter/angularjs';

import {EditorModelFactory, EditorModel} from './editor';
import { Experience } from '.';

export class ExperienceEditorController {
   static $inject = [
      '$stateParams',
      '$state',
      '$window',
      '$q',
      'dialogs',
      'loader',
      'toast',
      'gettextCatalog',
      'titleService',
      'experiencesService',
      'editorModelFactory',
      'experience', 'campaign' // route resolves
   ];

   constructor(
      $stateParams,
      private $state:StateService,
      private $window:ng.IWindowService,
      private $q:ng.IQService,
      private dialogs,
      private loader,
      private toast,
      private gettextCatalog,
      titleService,
      private experiencesService,
      editorModelFactory:EditorModelFactory,
      experience:Experience,
      campaign
   ) {
      this.typeSlug = $stateParams.slug;
      this.model = editorModelFactory($stateParams.slug, campaign, experience);
      if (this.model.init) {
         this.loader.showSpinner();
         this.model.init().finally(() => this.loader.hide());
      }

      this.gettextCatalog.setCurrentLanguage(this.model.language);
      experiencesService.bindExperiencePalette();
      titleService.setTitle(this.model.settings.name || this.model.typeDef.label);
   }

   typeSlug:string;
   model:EditorModel;
   saving:boolean;

   onLanguageChange(language:string) {
      this.gettextCatalog.setCurrentLanguage(language);
      this.model.language = language;
   }

   delete() {
      if (!this.model.isNew) {
         let message = 'Are you sure you want to delete this experience? This cannot be undone!';
         this.dialogs.confirm(message).then(() => {
            this.loader.showSpinner();
            this.experiencesService.del(this.model.experience.id)
               .finally(() => this.loader.hide())
               .then(() => {
                  this.toast.success()
                     .message('Experience deleted!')
                     .send();

                  this.$state.go('campaign.experiences');
               });
         });
      }
   }

   save() {
      const violations: string[] = [];
   
      const { settings } = this.model;

      // check for possible ADA color contrast violations; if any found, notify the user
      if (['web-reg', 'rsvp', 'join-virtual', 'media-page', 'qr-page'].includes(this.typeSlug)) {
         const checks = [
            { label: "Body background and font color", fg: settings.fontColor, bg: settings.backgroundColor },
            { label: "Body background and link color", fg: settings.linkColor || settings.accentColor, bg: settings.backgroundColor },
            { label: "Body background and title color", fg: settings.titleFontColor, bg: settings.backgroundColor, condition: !['media-page', 'qr-page'].includes(this.typeSlug) }, // title color is not present for QR or Media Sites
            { label: "Button text and button color", fg: settings.buttonTextColor, bg: settings.linkColor || settings.accentColor },
            { label: "Footer background and font color", fg: settings.footerFontColor, bg: settings.footerBackgroundColor },
            { label: "Footer background and link color", fg: settings.footerLinkColor, bg: settings.footerBackgroundColor },
         ];
         violations.push(...this.getContrastViolations(checks));
      } else if (this.typeSlug.endsWith('email')) {
         const checks = [
            { label: "Background and font color", fg: settings.footerFontColor, bg: settings.backgroundColor },
            { label: "Content background and content font color", fg: settings.fontColor, bg: settings.contentBackgroundColor },
            { label: "Link and background color", fg: settings.linkColor, bg: settings.backgroundColor },
            { label: "Button and button font color", fg: settings.buttonTextColor, bg: settings.buttonColor },
         ];
         violations.push(...this.getContrastViolations(checks));
      }

      if (violations.length > 0) {
         const message =
            `We noticed a few color choices that could make content harder to read for some users. Accessibility guidelines suggest a 4.5:1 contrast ratio for better readability.<br><br>` +
            violations.map(v => `• ${v}`).join('<br>') +
            `<br><br>Do you wish to continue anyway?`;

         this.dialogs.confirm(message).then(confirmed => {
            if (confirmed) {
               this.performSave();
            }
         });
      } else {
         this.performSave();
      }
   }

   performSave() {
      this.saving = true;
      this.model.validate().then(() => {
         if (this.model.onSave) {
            this.model.onSave();
         }

         if (this.model.isNew) {
            let promises = [
               this.experiencesService.create(this.model.experience)
            ];

            // siblings represent extra experiences to create
            if (this.model.siblings) {
               for (let s of this.model.siblings) {
                  // for each sibling check if it already exists, if not create
                  promises.push(this.experiencesService.hasExperienceType(s.campaign.id, s.typeDef).then(exists => {
                     if (!exists) {
                        return this.experiencesService.create(s.experience);
                     } else {
                        return this.$q.when();
                     }
                  }));
               }
            }

            return this.$q.all(promises).then(results => results[0]);
         } else {
            return this.experiencesService.update(this.model.experience);
         }
      }).then(experience => {
         if (this.model.isNew) {
            this.$state.go("campaign.experienceEdit", {slug: this.typeSlug, id: experience.id});
         } else {
            this.model.experience = experience;
         }

         this.toast.success()
            .message('Experience saved successfully')
            .send();
      }).catch(err => {
         if (ng.isArray(err)) { // assume array of errors is validation
            for (let e of err) {
               this.toast.error()
                  .title(e.invalid)
                  .message(e.detail)
                  .send();
            }
         } else if (err) {
            this.toast.error()
               .title('Failed to save experience')
               .formErrors(err)
               .send();
         }
      }).finally(() => this.saving = false);
   }

   test() {
      if (this.model.previewUrl) {
         this.$window.open(this.model.previewUrl);
      } else {
         this.model.test();
      }
   }

   hexToRgb(hex: string): [number, number, number] {
      const normalizedHex = hex.replace('#', '');
      const bigint = parseInt(normalizedHex.length === 3 ? normalizedHex.split('').map(c => c + c).join('') : normalizedHex, 16);
      const r = (bigint >> 16) & 255;
      const g = (bigint >> 8) & 255;
      const b = bigint & 255;
      return [r, g, b];
   }

   luminance(r: number, g: number, b: number): number {
      const srgb = [r, g, b].map(c => {
         const channel = c / 255;
         return channel <= 0.03928
            ? channel / 12.92
            : Math.pow((channel + 0.055) / 1.055, 2.4);
      });
      return 0.2126 * srgb[0] + 0.7152 * srgb[1] + 0.0722 * srgb[2];
   }

   contrastRatio(hex1: string, hex2: string): number {
      const [r1, g1, b1] = this.hexToRgb(hex1);
      const [r2, g2, b2] = this.hexToRgb(hex2);

      const lum1 = this.luminance(r1, g1, b1);
      const lum2 = this.luminance(r2, g2, b2);

      const brightest = Math.max(lum1, lum2);
      const darkest = Math.min(lum1, lum2);

      return (brightest + 0.05) / (darkest + 0.05);
   }

   isADACompliant(hex1: string, hex2: string, threshold = 4.5): boolean {
      if (!hex1 || !hex2) {
         return false;
      }

      const ratio = this.contrastRatio(hex1, hex2);
      return ratio >= threshold;
   }

   private getContrastViolations(checks: { label: string; fg: string; bg: string; condition?: boolean }[]): string[] {
      return checks
         .filter(check => check.condition !== false)
         .filter(check => !this.isADACompliant(check.bg, check.fg))
         .map(check => check.label);
   }
}

ng.module('e360')
   .controller('ExperienceEditorController', ExperienceEditorController);
